import {lockr, _} from '@api'
import {UPDATE_DATABASE_CONFIG} from '@api/graphql'
import {configs, nav, DeploymentConfig, onSiteError, globalStores as stores, globalStores, aws, site, ICatalogUserSettings, DEFAULT_DEPLOYMENT_CONFIG} from '@stores'
import {esClient} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {auth, authStore, jsonFn} from '@utils'
import axios from 'axios'
import {action, autorun, computed, observable, reaction, runInAction, trace} from 'mobx'
import {IC8DynamicConfig} from './IC8DynamicConfig'

export interface DevLoginInfo {
  label: string,
  login: string,
  password?: string
}

export interface TagType {
  id: string
  exId?: string
  createdAt: string
  updatedAt: string
  name: string
  description?: string
  displayName?: {
    singular?: string
    plural?: string
  }
  index?: number
  models?: string[]
}

/**
 * For internal use only
 *
 * Described an endpoint where a VIP application resides.  Often includes internal-only information like default logins/pws for various user accounts, environment (dev/uat/prod), customer, etc

 * Stored originally in devDeployments.js, now in vip-deployment in the VIP cloud elasticsearch deployment
 */
export interface ISiteDeployment {
  _id: string
  /**
   * Sometimes we have multiple configs in the same index so multiple devs can work on different configs at once on the same serer
   */
  configId?: string
  /**
   * Normally we query vip-config but sometimes new config work has structural changes so we can't insert a deployment into the existing index during dev
   */
  configIndex?: string

  /**
   * Deployments are grouped by this for display
   */
  heading?: string

  customer?: string

  environment?: string

  label?: string

  /** String to be appended to every label **/
  labelSuffix?: string

  tags?: string[]

  url?: string
  gatewayEndpoint?: string
  servicesUrl?: string
  secondaryServicesUrl?: string
  mediaUrl?: string
  centricURL?: string
  plmC8URL?: string
  imgProxyUrl?: string
  wsApiGatewayEndpoint?: string

  apiGatewayEndpoint?: string
  graphqlEndpoint?: string
  loginPageBgImage?: string
  leToken?: string

  /** Dev Only - see webpackDefines.js **/
  login?: string
  password?: string
  logins?: Array<DevLoginInfo>

  /**
   * Direct URL to ES - for testing cloud without API middleman
   */
  esUrl?: string
  esApiKey?: string
}

/**
 * Each unique site, such as:
 *    http://vip-c8-poc.centricsoftware.com/  (Restoration Hardware POC2)
 *
 */
export class DeploymentsStore {
  @observable initialized = false

  @computed get url() {
    return `${globalStores.es.client.url}/${this.selectedDeploymentId}`
  }

  constructor() {
    synchronizeStores(async () => {
      await this._ensureLoadedDeployments()

      await this._loadSelectedDeploymentFromQueryString()

      autorun(() => {
        if (!authStore.userIsAuthenticated) {
          this.hasLoadedDynamicConfig = false
          this.c8DynamicConfig = null
        }
      })

      autorun(async () => {
        // trace()
        if (site.loginConfig && authStore.userIsAuthenticated) {
          await aws.loadAWSConfig()
        }
      }, {name: 'Load application configuration (AWS)'})

      autorun(async () => {
        if (site.loginConfig && authStore.userIsAuthenticated) {
          await this.loadDeploymentConfig()
        }
      }, {name: 'Load application configuration'})

      autorun(async () => {
        const {deploymentConfig} = this, {axiosNest} = esClient, {users: {current}} = globalStores
        if (!deploymentConfig || !authStore.userIsAuthenticated) return
        // trace()
        await this.loadC8DynamicConfig()
      }, {name: `Load c8 dynamic globals`})

      reaction(() => this.selectedDeploymentId, id => {
        nav.updateQueryParams({deployment: id ?? undefined})
      })

      reaction(() => this.deploymentConfig, (deployment) => {
        if (deployment) {
          deployment.detailViews = deployment.detailViews ?? {}
        }
      })
    })
  }

  loadC8DynamicConfig = async () => {
    const {deploymentConfig} = this, {axiosNest} = esClient, {users: {current}} = globalStores
    if (axiosNest) {
      try {
        this.c8DynamicConfig = null
        this.hasLoadedDynamicConfig = false
        this.isLoadingDynamicConfig = true

        const maxFolderLevel = _.max((deploymentConfig?.workspaces ?? []).map(ws => ws?.merchFolderLevel ?? 0))
        // console.log(`load c8-globals`)
        const {data} = await esClient.axiosNest.get<IC8DynamicConfig>(`/c8-dynamic-globals?merchFolderLevel=${maxFolderLevel ?? 1}&user=${encodeURIComponent(current?.id)}`)

        this.c8DynamicConfig = data
        // console.log(data)
      } catch (err) {
        console.error(err)
      } finally {
        this.hasLoadedDynamicConfig = true
        this.isLoadingDynamicConfig = false
      }
    }
  }

  private _ensureLoadedDeployments = async() => {
    if (DEBUG && _.isEmpty(this.deployments)) {
      const client = axios.create({
        baseURL: VIP_ES_URL
      }), {es: {unwrapHitsResult}} = this
      client.defaults.headers.common['Authorization'] = `ApiKey ${VIP_ES_API_KEY}`

      const r = await client.post('vip-deployment/_search', { size: 10000 }),
      {items} = unwrapHitsResult<ISiteDeployment>(r.data)
      // console.log(r, items)
      this.deployments = items
    }
  }

  @action private _loadSelectedDeploymentFromQueryString = async () => {
    const deployment = nav.queryParams.deployment ?? stores.config.originalConfig._id,
      {deployments} = this

    if (deployment) {
      const selected = deployments?.find(d => this.idFor(d) === deployment)
      if (selected) {
        await this.navigateToDeployment(selected, null, true)
      } else {
        DEBUG && console.warn(`Unable to find specified deployment ${deployment}`)
      }
    }

    this.initialized = true
  }

  /**
   * Ideally this would be an observable.ref as we are now watching 1000s of properties for changes that we don't expect to ever happen,
   * however there are a few 'post-load' fixes like adding missing sections that wouldn't work without this
   */
  @observable deploymentConfig: DeploymentConfig
  @observable.ref error: Error

  get es() { return globalStores.es}

  @observable hasLoadedDynamicConfig = false
  @observable isLoadingDynamicConfig = false

  @action loadDeploymentConfig = async () => {
    const {es} = this
    if (authStore.userIsAuthenticated && es.axiosInstance && es.axiosServicesInstance) {
      // Authenticated User gets full deployment config, unknown non-user gets surface level config (login page URL, default login, company contacts, etc)

      authStore.userIsAuthenticated && await authStore.verifyTokenNotExpired()
      const {selectedDeployment} = this

      const vipConfigId = selectedDeployment?.configId ?? 'default',
        deploymentIndex = selectedDeployment?.configIndex ?? es.indexes.config

      runInAction(`Begin loading deployment config '${vipConfigId}'`, () => {
        this.deploymentConfig = null
        this.error = null
        this.tagTypes = []
      })

      try {
        // console.log({useCloudFront, deploymentConfig})
        await runInAction(`Save deployment config from database to instance variable`, async () => {
          const {useCloudFront} = aws

          // console.log(`load site config`)
          const deploymentConfigQueryResult = useCloudFront && false ? await aws.cfClient.get('site-config.json', {transformResponse: []}) // prevent built in json parsing
                                                                     : await es.client.get({index: deploymentIndex, id: vipConfigId}),
            deploymentConfig = _.isString(deploymentConfigQueryResult?.ui) ? jsonFn.parse(deploymentConfigQueryResult.ui) : jsonFn.parse(deploymentConfigQueryResult?.data)

          this.deploymentConfig = _.merge({}, DEFAULT_DEPLOYMENT_CONFIG, deploymentConfig)
        })
      } catch (err) {
        runInAction(`Error loading deployment config`, () => {
          this.error = err
          console.error(err)
          this.deploymentConfig = null
          DEBUG && onSiteError(new Error(`Unable to load deployment '${deploymentIndex}.${vipConfigId}'.  ${err.message}\nIn release mode this will route to logout page.`))
          !DEBUG && authStore.logout()
        })
      }
    } else {
      this.deploymentConfig = null

      if (!authStore.userIsAuthenticated) {
        console.warn(`Can't load deployment config - not logged in`)
      }
    }
  }

  @observable.ref tagTypes: Array<TagType> = []

  @computed get tagTypeById() {
    const {tagTypes, deploymentConfig: {workspaces}} = this
    return _.keyBy(tagTypes, tt => tt.id)
  }

  @computed get tagTypeByIndex() {
    const {tagTypes, deploymentConfig: {workspaces}} = this
    return _.chain(tagTypes).map(tt => !tt.models ? workspaces.filter(ws => ws.catalog).map(({catalog: {index}}) => ({...tt, esIndex: index})) : tt.models.map(m => ({...tt, esIndex: m}))).flatten().groupBy(tt => tt.esIndex).value()
  }

  @computed get queryParams() {
    const {selectedDeployment, idFor} = this, {config: {originalConfig, config}} = stores
    if (DEBUG && originalConfig != config) {
      return {
        deployment: idFor(selectedDeployment) ?? undefined
      }
    }
    return {}
  }

  /**
   * This is empty in production
   */
  @observable.ref deployments: Array<ISiteDeployment> = []

  /**
   * This is undefined in production
   */
  @observable selectedDeploymentId: string

  @computed get deploymentByHeading() {
    return _.groupBy(this.deployments, d => d.heading ?? d.customer ?? '')
  }

  @computed get deploymentById() {
    return _.groupBy(this.deployments, d => this.idFor(d))
  }

  @computed get deploymentEnv() {
    const {selectedDeployment} = this

    const tags = new Set<string>(selectedDeployment?.tags ?? [])
    if (tags.has('prod') || tags.has('production')) {
      return 'prod'
    }
    else if (tags.has('uat')) {
      return 'uat'
    }

    return DEBUG ? 'dev' : 'prod'
  }

  @computed.struct get selectedDeployment(): ISiteDeployment {
    const {deployments, idFor, selectedDeploymentId} = this
    const d = deployments.find(
      d => {
        const id = this.idFor(d)
        return id == this.selectedDeploymentId || (!selectedDeploymentId && (configs.config.apiGatewayEndpoint.startsWith(d.url) || configs.config._id == id))
      }) ?? _.first(this.deployments)

    return {...d, _id: idFor(d)}
  }

  @computed get devLoginInfo(): DevLoginInfo {
    if (DEBUG) {
      const {selectedDeployment} = this, {queryParams: {devLogin}} = nav, {user} = authStore
      if (selectedDeployment && (devLogin || user)) {
        return selectedDeployment.logins?.find(l => l.login === devLogin || l.login === user?.username)
      }
    }
    return null
  }

  urlForDeployment = (deployment: ISiteDeployment) => {
    return nav.urlFor({})
  }

  @action navigateToDeployment = async (deployment: ISiteDeployment, loginInfo?: { login: string, password: string }, initialLoad = false) => {
    const {centricURL, ...d} = deployment,
      id = this.idFor(deployment)

    lockr.prefix = `${id}--`
    !initialLoad && console.debug(`Navigating to '${id}' "${this.labelFor(deployment)}" (${deployment.url})`)
    if (this.selectedDeploymentId == id) {
      if (loginInfo) {
        await auth.invalidateUser()
        nav.updateQueryParams({devLogin: loginInfo?.login ?? undefined})
      }
      return
    }

    //console.log(`Navigate to ${_id}`)
    const differentCentric = !initialLoad && this.selectedDeployment?.centricURL != centricURL
    this.selectedDeploymentId = id
    if (differentCentric && auth.authStore.userIsAuthenticated) {
      await auth.invalidateUser()
    }

    nav.updateQueryParams({deployment: id, devLogin: loginInfo?.login ?? undefined})

    const {sidebar, filter, catalog} = stores
    sidebar.reset(initialLoad)

    if (!initialLoad) {
      filter.resetFilters()
      catalog.reset()
    }

    //console.log(configStore.config)
  }

  @action downloadCurrentConfig = () => {
    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(JSON.stringify(this.deploymentConfig, null, 2)))
    element.setAttribute('download', `${window.location.hostname}-config.json`)

    element.style.display = 'none'
    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)
  }

  @action uploadConfig = () => {

  }

  updateDBConfig = () => {
    const res = stores.apollo.client.mutate({
      mutation: UPDATE_DATABASE_CONFIG,
      variables: {input: {}}
    })
  }

  labelFor = (deployment: ISiteDeployment) => {
    if (!deployment) return undefined

    const {label, labelSuffix, environment, url, customer} = deployment

    return `${label || customer || url}${labelSuffix ?? ''}`
  }

  /**
   * This id is serialized on to the url and is expected to be something like asics-dev-test
   * @param _id
   * @param customer
   * @param environment
   * @param tags
   */
  idFor = (deployment: ISiteDeployment) => {
    if (!deployment) return undefined

    const {_id, customer, environment, tags, configId} = deployment
    return _id ?? [customer, environment, configId, ...(tags ?? [])]
      .filter(_.identity)
      .map(s =>
        s
          .replaceAll(' ', '-')
          .toLowerCase()
      ).join('-')
  }

  @observable.ref c8DynamicConfig: IC8DynamicConfig

  @action invalidateC8DynamicConfig = async () => {
    const {loadC8DynamicConfig, es: {client: {axiosNest}}} = this
    this.hasLoadedDynamicConfig = false
    this.c8DynamicConfig = null
    await axiosNest.delete('c8/cache-reset') // TODO - this should be done by the server, not random clients calling it
    await loadC8DynamicConfig()
  }

  @computed get enumByName() {
    const {c8DynamicConfig} = this
    if (!c8DynamicConfig?.centricEnumValues) { return {} }

    return c8DynamicConfig.centricEnumValues
  }

  @computed get hostName() {
    const {selectedDeployment} = this
    return selectedDeployment?.url?.match(/\/\/(.*?)\./)?.[1]
  }

  @computed get isNewSync() {
    const {deploymentConfig: {syncVersion}} = this
    return syncVersion && syncVersion == "2.0"
  }
}

export const deployments = new DeploymentsStore()

/**
 * Current customer configuration
 *    In DEBUG mode this can be changed on the fly in a dropdown menu via {@see deployments.deploymentConfig}
 *    Real sites have JSON serialized in an ES vip-config index somewhere
 */
export const cc = deployments.deploymentConfig


// WEBPACK FOOTER //
// ./src/stores/dev/DeploymentsStore.ts